import React from 'react';
import './App.css';
import Survey from './components/Survey.tsx';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import rawConfig from './config/surveyConfig_2.json';
import { UserResponse } from './types/survey.tsx';

function App() {
  const config = rawConfig;
  
  // Find the photos step index
  const getPhotosStepIndex = () => {
    return config.steps.findIndex(step => step.id === 'photos');
  };

  const [currentStep, setCurrentStep] = React.useState(() => {
    // Check URL parameters for saved step
    const params = new URLSearchParams(window.location.search);
    try {
      const savedStep = params.get('step');
      if (savedStep) {
        const decodedStep = parseInt(atob(savedStep));
        // Ensure the step is valid
        if (!isNaN(decodedStep) && decodedStep >= 0 && decodedStep < config.steps.length) {
          return decodedStep;
        }
      }
      return 0;
    } catch (e) {
      console.error('Error parsing step from URL:', e);
      return 0;
    }
  });
  
  // Initialize history with all steps up to current step
  const [history, setHistory] = React.useState<number[]>(() => {
    const steps: number[] = [];
    for (let i = 0; i <= currentStep; i++) {
      steps.push(i);
    }
    return steps;
  });

  const [isCompleted, setIsCompleted] = React.useState(false);
  const [responses, setResponses] = React.useState<Record<string, any>>(() => {
    // Check URL parameters for saved responses
    const params = new URLSearchParams(window.location.search);
    try {
      const savedResponses = params.get('responses');
      return savedResponses ? JSON.parse(atob(savedResponses)) : {};
    } catch (e) {
      return {};
    }
  });
  const logo = require('./assets/logo.png');

  const handleBack = () => {
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop();
      const previousStep = newHistory[newHistory.length - 1];
      
      // Get visible steps based on current responses
      const visibleSteps = config.steps.filter(s => {
        if (!s.showWhen) return true;
        const [dependentStepId, requiredValue] = Object.entries(s.showWhen)[0];
        if (dependentStepId === 'metal-type') {
          const selectedMetals = responses[dependentStepId] as string[] || [];
          return selectedMetals.includes(requiredValue as string);
        }
        return responses[dependentStepId] === requiredValue;
      });

      // Find the previous visible step
      const currentVisibleIndex = visibleSteps.findIndex(s => s.id === config.steps[currentStep].id);
      if (currentVisibleIndex > 0) {
        const previousVisibleStep = visibleSteps[currentVisibleIndex - 1];
        const previousStepIndex = config.steps.findIndex(s => s.id === previousVisibleStep.id);
        
        setHistory(newHistory);
        setCurrentStep(previousStepIndex);
      }
    }
  };

  // Generate URL with current responses
  const generateSurveyUrl = () => {
    const baseUrl = window.location.origin + window.location.pathname;
    const encodedResponses = btoa(JSON.stringify(responses));
    const photosStepIndex = getPhotosStepIndex();
    const encodedStep = btoa(photosStepIndex.toString());
    return `${baseUrl}?responses=${encodedResponses}&step=${encodedStep}`;
  };

  // QR code component for file upload steps
  const SurveyQRCode = () => (
    <div className="qr-code-container">
      <p>Continue on your phone to take photos</p>
      <QRCode value={generateSurveyUrl()} size={200} />
    </div>
  );

  const handleSurveyComplete = (responses: UserResponse[]) => {
    console.log('Survey completed with responses:', responses);
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          {history.length > 1 && !isCompleted && (
            <button className="back-button" onClick={handleBack}>
              ← Back
            </button>
          )}
          <img src={logo} className="App-logo" alt="logo" />
        </div>
      </header>
      <main>
        <Survey 
          currentStep={currentStep} 
          setCurrentStep={setCurrentStep}
          history={history}
          setHistory={setHistory}
          setIsCompleted={setIsCompleted}
          responses={responses}
          setResponses={setResponses}
          QRCodeComponent={SurveyQRCode}
          onComplete={handleSurveyComplete}
        />
      </main>
    </div>
  );
}

export default App;
